import type { HTMLAttributes, ReactNode } from 'react';
import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { tones, type ToneName } from '@/theme';

/**
 * Aviso en línea del sistema de diseño.
 *
 * Se usa para el mensaje que ya trae el backend dentro de `ApiError` (`getErrorMessage`) y
 * para notas del formulario (ej. "revisa los campos marcados"). Usa las mismas píldoras de
 * color que `Badge` (`tones`) para no introducir un estilo nuevo por pantalla.
 *
 * Se pinta con `role="alert"`: aparece después de un envío fallido, así que los lectores de
 * pantalla lo anuncian.
 */

/** Icono por defecto de cada tono (se puede sustituir con la prop `icon`). */
const defaultIcons: Record<ToneName, ReactNode> = {
  neutral: <Info size={16} />,
  info: <Info size={16} />,
  brand: <Info size={16} />,
  success: <CircleCheck size={16} />,
  warning: <TriangleAlert size={16} />,
  danger: <CircleAlert size={16} />,
};

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  tone?: ToneName;
  /** Sustituye el icono del tono. */
  icon?: ReactNode;
}

export function Alert({ tone = 'danger', icon, className, children, ...props }: AlertProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-2.5 rounded-panel border px-3.5 py-3 text-caption',
        tones[tone].className,
        className,
      )}
      {...props}
    >
      <span className="mt-0.5 shrink-0" aria-hidden="true">
        {icon ?? defaultIcons[tone]}
      </span>

      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
